import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ApartmentRounded,
  HistoryRounded,
  PersonRounded,
  SupervisorAccountRounded,
} from '@mui/icons-material';

import {
  getStacOrganizationEditorUnitHistory,
  getStacOrganizationEditorWorkerHistory,
} from '../services/stacService';
import type {
  StacOrgEditorPlacementHistoryEntry,
  StacOrgEditorUnit,
  StacOrgEditorWorker,
} from '../types/stac';


interface StacOrganizationHistoryDialogProps {
  open: boolean;
  onClose: () => void;

  unit: StacOrgEditorUnit | null;
  worker: StacOrgEditorWorker | null;
}


function formatDateTime(
  value: string | null,
): string {
  if (!value) {
    return 'Actual';
  }

  const parsed = new Date(value);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'short',
      timeStyle: 'short',
    },
  ).format(parsed);
}


function Period({
  validFrom,
  validTo,
}: {
  validFrom: string;
  validTo: string | null;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <Typography
        sx={{
          color: '#94a3b8',
          fontSize: '0.78rem',
        }}
      >
        {formatDateTime(validFrom)}
        {' → '}
        {validTo
          ? formatDateTime(validTo)
          : 'Actual'}
      </Typography>

      {!validTo ? (
        <Chip
          size="small"
          label="Actual"
          variant="outlined"
          sx={{
            color: '#7dd3fc',
          }}
        />
      ) : null}
    </Stack>
  );
}


function ChangeActor({
  name,
}: {
  name: string | null;
}) {
  if (!name) {
    return null;
  }

  return (
    <Typography
      sx={{
        mt: 0.4,
        color: '#64748b',
        fontSize: '0.74rem',
      }}
    >
      Cambio realizado por {name}
    </Typography>
  );
}


function PlacementRow({
  entry,
}: {
  entry: StacOrgEditorPlacementHistoryEntry;
}) {
  const legacy =
    entry.parent_node_type === 'worker';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border:
          legacy
            ? '1px solid rgba(245,158,11,0.28)'
            : '1px solid rgba(148,163,184,0.12)',
        bgcolor:
          legacy
            ? 'rgba(245,158,11,0.04)'
            : 'rgba(2,6,23,0.28)',
        p: 1.4,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
      >
        {entry.parent_node_type
          === 'unit' ? (
          <ApartmentRounded
            sx={{
              color: '#38bdf8',
              fontSize: 19,
            }}
          />
        ) : (
          <PersonRounded
            sx={{
              color: legacy
                ? '#fbbf24'
                : '#38bdf8',
              fontSize: 19,
            }}
          />
        )}

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: '#f8fafc',
              fontWeight: 850,
            }}
          >
            {entry.parent_display_name}
          </Typography>

          {legacy ? (
            <Typography
              sx={{
                color: '#fbbf24',
                fontSize: '0.72rem',
              }}
            >
              Relación heredada del modelo anterior
            </Typography>
          ) : null}
        </Box>
      </Stack>

      <Box sx={{ mt: 0.8 }}>
        <Period
          validFrom={entry.valid_from}
          validTo={entry.valid_to}
        />

        <ChangeActor
          name={
            entry.changed_by_display_name
          }
        />
      </Box>
    </Paper>
  );
}


function EmptyHistory({
  children,
}: {
  children: string;
}) {
  return (
    <Typography
      sx={{
        color: '#64748b',
        fontSize: '0.82rem',
      }}
    >
      {children}
    </Typography>
  );
}


export default function StacOrganizationHistoryDialog({
  open,
  onClose,
  unit,
  worker,
}: StacOrganizationHistoryDialogProps) {
  const unitHistoryQuery = useQuery({
    queryKey: [
      'stac-organization-editor',
      'unit-history',
      unit?.id,
    ],
    queryFn: () =>
      getStacOrganizationEditorUnitHistory(
        unit!.id
      ),
    enabled:
      open
      && Boolean(unit),
    retry: false,
  });

  const workerHistoryQuery = useQuery({
    queryKey: [
      'stac-organization-editor',
      'worker-history',
      worker?.worker_id,
    ],
    queryFn: () =>
      getStacOrganizationEditorWorkerHistory(
        worker!.worker_id
      ),
    enabled:
      open
      && Boolean(worker),
    retry: false,
  });

  const loading =
    unit
      ? unitHistoryQuery.isLoading
      : workerHistoryQuery.isLoading;

  const hasError =
    unit
      ? unitHistoryQuery.isError
      : workerHistoryQuery.isError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#0f172a',
          backgroundImage: 'none',
          color: '#f8fafc',
          border:
            '1px solid rgba(148,163,184,0.18)',
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <HistoryRounded
            sx={{
              color: '#38bdf8',
            }}
          />

          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
                fontSize: '1.05rem',
              }}
            >
              Histórico organizativo
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.8rem',
              }}
            >
              {unit?.name
                ?? worker?.display_name
                ?? 'STAC'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ py: 2 }}
          >
            <CircularProgress size={20} />

            <Typography
              sx={{
                color: '#94a3b8',
              }}
            >
              Cargando histórico...
            </Typography>
          </Stack>
        ) : hasError ? (
          <Alert
            severity="error"
            variant="outlined"
          >
            No se pudo cargar el histórico
            organizativo.
          </Alert>
        ) : unit ? (
          <Stack spacing={2}>
            <Box>
              <Typography sx={sectionTitleSx}>
                Nombre de la unidad
              </Typography>

              <Typography sx={sectionHelpSx}>
                Cambios de denominación
                conservados en el tiempo.
              </Typography>
            </Box>

            {unitHistoryQuery.data
              ?.names.length ? (
              <Stack spacing={1}>
                {unitHistoryQuery.data.names.map(
                  (entry) => (
                    <Paper
                      key={entry.id}
                      elevation={0}
                      sx={historyRowSx}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                        }}
                      >
                        {entry.name}
                      </Typography>

                      <Box sx={{ mt: 0.6 }}>
                        <Period
                          validFrom={
                            entry.valid_from
                          }
                          validTo={
                            entry.valid_to
                          }
                        />

                        <ChangeActor
                          name={
                            entry
                              .changed_by_display_name
                          }
                        />
                      </Box>
                    </Paper>
                  ),
                )}
              </Stack>
            ) : (
              <EmptyHistory>
                No hay cambios de nombre registrados.
              </EmptyHistory>
            )}

            <Divider />

            <Box>
              <Typography sx={sectionTitleSx}>
                Posición en el organigrama
              </Typography>

              <Typography sx={sectionHelpSx}>
                Unidad superior aplicable
                en cada periodo.
              </Typography>
            </Box>

            {unitHistoryQuery.data
              ?.placements.length ? (
              <Stack spacing={1}>
                {unitHistoryQuery.data.placements.map(
                  (entry) => (
                    <PlacementRow
                      key={entry.id}
                      entry={entry}
                    />
                  ),
                )}
              </Stack>
            ) : (
              <EmptyHistory>
                No hay movimientos organizativos registrados.
              </EmptyHistory>
            )}

            <Divider />

            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <SupervisorAccountRounded
                  sx={{
                    color: '#38bdf8',
                    fontSize: 20,
                  }}
                />

                <Typography sx={sectionTitleSx}>
                  Responsabilidad principal
                </Typography>
              </Stack>

              <Typography sx={sectionHelpSx}>
                Responsables asignados
                directamente a esta unidad.
              </Typography>
            </Box>

            {unitHistoryQuery.data
              ?.managers.length ? (
              <Stack spacing={1}>
                {unitHistoryQuery.data.managers.map(
                  (entry) => (
                    <Paper
                      key={entry.id}
                      elevation={0}
                      sx={historyRowSx}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                        }}
                      >
                        {entry.display_name}
                      </Typography>

                      <Box sx={{ mt: 0.6 }}>
                        <Period
                          validFrom={
                            entry.valid_from
                          }
                          validTo={
                            entry.valid_to
                          }
                        />

                        <ChangeActor
                          name={
                            entry
                              .changed_by_display_name
                          }
                        />
                      </Box>
                    </Paper>
                  ),
                )}
              </Stack>
            ) : (
              <EmptyHistory>
                Esta unidad no ha tenido
                responsable principal propio.
              </EmptyHistory>
            )}
          </Stack>
        ) : worker ? (
          <Stack spacing={2}>
            <Box>
              <Typography sx={sectionTitleSx}>
                Ubicación organizativa
              </Typography>

              <Typography sx={sectionHelpSx}>
                Historial de unidades
                principales de esta persona.
              </Typography>
            </Box>

            {workerHistoryQuery.data
              ?.placements.length ? (
              <Stack spacing={1}>
                {workerHistoryQuery.data.placements.map(
                  (entry) => (
                    <PlacementRow
                      key={entry.id}
                      entry={entry}
                    />
                  ),
                )}
              </Stack>
            ) : (
              <EmptyHistory>
                No hay movimientos organizativos registrados.
              </EmptyHistory>
            )}
          </Stack>
        ) : (
          <EmptyHistory>
            No hay un elemento seleccionado.
          </EmptyHistory>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}


const sectionTitleSx = {
  color: '#f8fafc',
  fontWeight: 900,
};

const sectionHelpSx = {
  mt: 0.2,
  color: '#64748b',
  fontSize: '0.78rem',
};

const historyRowSx = {
  borderRadius: 2.5,
  border:
    '1px solid rgba(148,163,184,0.12)',
  bgcolor:
    'rgba(2,6,23,0.28)',
  p: 1.4,
};